import LinkIcon from '@mui/icons-material/Link';
import { Collapse, Stack } from '@mui/material';
import { useSession } from '@side/next-auth/react';
import { Alert, AlertButton } from '@side/pantry';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useGetOauthRequestUrl } from '@/common/hooks/useGetOauthRequestUrl';
import { useUserInformation } from '@/common/hooks/useUserInformation/useUserInformation';
import { navigateTo } from '@/utils/navigation';
import {
  ContactSyncAccountType,
  TrackContactSyncEvents,
  useTrackContactSyncAnalytics,
} from '$analytics/useTrackAnalytics/domains/contactSync';
import { useIgnoredInteractionIdsForContactBanner } from './ContactsIntegrationBanner.helpers';
import { contactsIntegrationModalOpen } from './ContactsIntegrationModal.state';

type ContactsIntegrationBannerProps = {
  show: boolean;
  interactionId?: string;
  interactionOwnerId?: string;
  googleAccountLinked?: boolean;
  followUpBossAccountLinked?: boolean;
  compact?: boolean;
};

export const ContactsIntegrationBanner = ({
  show,
  interactionId,
  interactionOwnerId,
  googleAccountLinked,
  followUpBossAccountLinked,
  compact,
}: ContactsIntegrationBannerProps) => {
  const { data: session } = useSession();
  const { legacyExternalId, id: userId } = session?.user ?? {};
  const userIsInteractionOwner =
    interactionOwnerId === legacyExternalId || interactionOwnerId === userId;

  const setDialogOpen = useSetAtom(contactsIntegrationModalOpen);
  const { isBannerIgnoredForInteractionId, ignoreBannerForInteractionId } =
    useIgnoredInteractionIdsForContactBanner();
  const {
    getOauthRequestUrl: getFollowUpBossRequestUrl,
    loading: followUpBossRedirectUrlLoading,
  } = useGetOauthRequestUrl({
    provider: 'follow-up-boss',
    returnPath: window.location.pathname,
    accountId: userId ?? '',
    accountType: 'user',
    errorMessage:
      'There was a problem connecting contacts. Please refresh and try again.',
  });
  const interactionOwnerInfo = useUserInformation(
    !userIsInteractionOwner ? interactionOwnerId : undefined,
  );
  const trackEvent = useTrackContactSyncAnalytics();

  const {
    getOauthRequestUrl: getGoogleRequestUrl,
    loading: googleRedirectUrlLoading,
  } = useGetOauthRequestUrl({
    provider: 'google',
    returnPath: window.location.pathname,
    accountId: userId ?? '',
    accountType: 'user',
    errorMessage:
      'There was a problem connecting contacts. Please refresh and try again.',
  });

  const onIgnoreClick = () => {
    if (!interactionId) {
      return;
    }
    ignoreBannerForInteractionId(interactionId);
    trackEvent(TrackContactSyncEvents.ClickIgnoreButton);
  };

  const onConnectClick = useCallback(async () => {
    if (!userId) {
      return;
    }
    if (!googleAccountLinked && !followUpBossAccountLinked) {
      setDialogOpen(true);
      trackEvent(TrackContactSyncEvents.MountModal);
      return;
    }
    const requestUrl = !followUpBossAccountLinked
      ? await getFollowUpBossRequestUrl()
      : await getGoogleRequestUrl();
    if (requestUrl) {
      trackEvent(TrackContactSyncEvents.ClickConnectButton, {
        eventContainer: 'contact_dropdown',
        accountType: !followUpBossAccountLinked
          ? ContactSyncAccountType.FollowUpBoss
          : ContactSyncAccountType.Google,
      });
      navigateTo(requestUrl);
    }
  }, [
    userId,
    googleAccountLinked,
    followUpBossAccountLinked,
    getFollowUpBossRequestUrl,
    getGoogleRequestUrl,
    setDialogOpen,
    trackEvent,
  ]);

  if (
    !show ||
    !userId ||
    !interactionId ||
    !interactionOwnerId ||
    (!!googleAccountLinked && !!followUpBossAccountLinked)
  ) {
    return null;
  }

  const interactionOwnerName =
    interactionOwnerInfo?.displayName ?? 'the primary agent';
  const captionParts = [
    userIsInteractionOwner
      ? 'Connect your'
      : `Ask ${interactionOwnerName} to connect their`,
  ];
  if (!googleAccountLinked && !followUpBossAccountLinked) {
    captionParts.push('Google or Follow Up Boss accounts for more results');
  } else if (!googleAccountLinked) {
    captionParts.push('Google account for more results');
  } else {
    captionParts.push('Follow Up Boss account for more results');
  }

  // Temp id for pendo, can be removed after pendo guide is turned off
  const bothUnlinked = !googleAccountLinked && !followUpBossAccountLinked;
  let tempPendoId = '';
  if (bothUnlinked) {
    tempPendoId = 'both-connect';
  } else if (!googleAccountLinked) {
    tempPendoId = 'google-connect';
  } else {
    tempPendoId = 'follow-up-boss-connect';
  }

  return (
    <Collapse in={!isBannerIgnoredForInteractionId(interactionId)}>
      <Alert
        severity="info"
        icon={userIsInteractionOwner ? false : <LinkIcon />}
        action={
          <Stack direction="row" sx={{ gap: 2, justifyContent: 'flex-end' }}>
            <AlertButton
              severity="info"
              variant="outlined"
              onClick={onIgnoreClick}
            >
              Ignore
            </AlertButton>
            {userIsInteractionOwner && (
              <AlertButton
                id={tempPendoId}
                severity="primary"
                disabled={
                  followUpBossRedirectUrlLoading || googleRedirectUrlLoading
                }
                loading={
                  followUpBossRedirectUrlLoading || googleRedirectUrlLoading
                }
                variant="contained"
                onClick={() => void onConnectClick()}
              >
                Connect
              </AlertButton>
            )}
          </Stack>
        }
        sx={(theme) => ({
          mb: 3,
          ...(compact
            ? {
                '.MuiAlert-message': {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  '> div:nth-of-type(2)': {
                    margin: theme.spacing(2, 0, 0, 0),
                    width: '100%',
                  },
                },
              }
            : {}),
        })}
      >
        {captionParts.join(' ')}
      </Alert>
    </Collapse>
  );
};
